import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Column, Row, useTable } from "react-table";
import moment from "moment";
import { useStateMachine } from "little-state-machine";
import { useNavigate } from "react-router-dom";
import { Col, OverlayTrigger, Tooltip } from "react-bootstrap";

import { getShipmentsByIds } from "api";
import getLabel from "helpers/getLabel";
import { getFreightModeIcon } from "helpers/freightModeHelper";
import Badge from "components/atoms/Badge/Badge";
import DataTable from "components/organisms/DataTable/DataTable";
import { QuoteStatusColors } from "constants/";
import { updateShipmentIdAndTitle } from "store/store";

type ImportQuoteListProps = {
  row: any;
};

function formatDate(date: any) {
  // handle case when user does not select any date from pickup or delivery date and return -
  if (date === " ") return " -";
  if (date === "ASAP") return "ASAP";

  // handle draft save for pickup date and delivery date
  if (date && date !== " ") {
    const splitDate = date.split(" ");
    // after split index 0 and index 1 if same date and return ASAP
    if (splitDate[0] === splitDate[1]) return "ASAP";
  }
  return moment(date).format("DD MMM YYYY");
}

function ImportQuoteList(props: ImportQuoteListProps) {
  const { row } = props;
  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const { actions } = useStateMachine({ updateShipmentIdAndTitle });
  const navigate = useNavigate();
  const intl = useIntl();

  useEffect(() => {
    if (!row?.original?.metadata?.imported_objects_references) {
      return;
    }
    setFetching(true);
    getShipmentsByIds({
      params: {
        shipment_references: row.original.metadata.imported_objects_references.join(","),
        properties: ["expected_pickup_datetime", "expected_delivery_datetime"].join(","),
      },
    })
      .then((res) => {
        setData(res.data);
      })
      .catch(() => {})
      .finally(() => {
        setFetching(false);
      });
  }, []);

  const columns = useMemo<Column[]>(
    () => [
      {
        id: "modified",
        Header: intl.formatMessage({
          id: "general.shipment",
          defaultMessage: "Shipment",
        }),
        accessor: "shipment_id",
        Cell: ({ value, row }: any) => (
          <>
            <p className="font-weight-bold mb-0">{value || "-"}</p>
            <span className="mt-2">{row?.original?.title || "-"}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage(
          {
            id: "general.paymentLocation",
            defaultMessage: "PAYMENT LOCATION",
          },
          { paymentLocation: getLabel("Payment location", "capitalize") },
        ),
        accessor: "payment_location",
        Cell: ({ value, row }: any) => (
          <>
            <p className="mb-0">{value?.label || ""}</p>
            <span className="text-primary mt-2">
              <OverlayTrigger
                placement="top"
                delay={{ show: 50, hide: 50 }}
                overlay={<Tooltip id="button-tooltip">{row.original?.created_by?.email}</Tooltip>}
              >
                <span>{row.original?.created_by?.name}</span>
              </OverlayTrigger>
            </span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({
          id: "quote.listing.quotation",
          defaultMessage: "Quotation",
        }),
        id: "quotations",
        accessor: "quotes_count",
        Cell: ({ value }: any) => <div>{value || "-"}</div>,
      },
      {
        Header: intl.formatMessage({
          id: "shipments.freightModeTableHeader",
          defaultMessage: "Freight Mode",
        }),
        accessor: "freight_mode",
        Cell: ({ value }: any) => (
          <div>
            {value?.length > 0
              ? value.map((item: any, index: number) => {
                  const Icon = getFreightModeIcon(item.name, item.type, true);
                  return (
                    <span className="mr-2" key={index}>
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id="freight-mode">
                            <span className="font-weight-normal">{item.name}</span>
                          </Tooltip>
                        }
                      >
                        <Icon />
                      </OverlayTrigger>
                    </span>
                  );
                })
              : "-"}
          </div>
        ),
      },
      {
        id: "route",
        Header: intl.formatMessage({
          id: "quote.listing.fromTo",
          defaultMessage: "From-to",
        }),
        accessor: "pickup_city",
        Cell: ({ value, row }: any) => {
          if (!value && !row.original?.delivery_city) return <div className="mb-0">-</div>;
          return (
            <Col className="pl-0">
              <p className="mb-2">{value ? `${value}: ${formatDate(row.original?.expected_pickup_datetime)}` : "-"}</p>
              <p className="mb-2">
                {row.original?.delivery_city
                  ? `${row.original?.delivery_city}: ${formatDate(row.original?.expected_delivery_datetime)}`
                  : "-"}
              </p>
            </Col>
          );
        },
      },
      {
        id: "status",
        Header: intl.formatMessage({
          id: "general.status",
          defaultMessage: "Status",
        }),
        accessor: "status",
        Cell: ({ value: cellValue }: any) => {
          if (!cellValue) return "-";
          const sortedStatusArr = cellValue.sort((a: any, b: any) => {
            const priority = ["Approval pending", "Awaiting action", "Closed for quotations", "Open for quotations"];
            return priority.indexOf(a.value) - priority.indexOf(b.value);
          });
          const value = sortedStatusArr[0];
          return (
            <div className="d-flex flex-column align-items-start">
              <div className="d-flex flex-column mb-2">
                <Badge type={QuoteStatusColors[value] || "secondary"} label={value} />
              </div>
            </div>
          );
        },
      },
    ],
    [],
  );

  const instance = useTable({
    columns,
    data,
  });

  const handleRowClick = (row: Row<any>, e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    const reference = row.original?.shipment_id;
    const title = row.original?.title;
    actions.updateShipmentIdAndTitle({
      shipmentId: reference,
      shipmentTitle: title,
    });

    if (e.ctrlKey || e.metaKey) window.open(`/shipment/${reference}`, "_blank");
    else navigate(`/shipment/${reference}`, { state: { data: row.original?.created_by } });
  };

  return (
    <div>
      <DataTable tableInstance={instance} draggable={false} isLoading={fetching} onRowClick={handleRowClick} />
    </div>
  );
}

export default ImportQuoteList;
